import { CaretDown, Check, MagnifyingGlass } from '@/shared/icons/phosphor'
import { Combobox } from '@kobalte/core/combobox'
import { type Component, type JSX, Show, createMemo, createSignal } from 'solid-js'

export type RichSelectIndicator = 'success' | 'available' | 'warning' | 'error' | 'neutral'

export interface RichSelectOption<T extends string = string> {
  value: T
  label: string
  description?: string
  keywords?: string[]
  disabled?: boolean
  indicator?: RichSelectIndicator
  leading?: () => JSX.Element
}

export function foldForSearch(text: string | undefined): string {
  if (!text) {
    return ''
  }
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}

export type RichSelectSize = 'sm' | 'md' | 'lg'

export interface RichSelectProps<T extends string = string> {
  value: T | undefined
  onChange: (value: T) => void
  options: RichSelectOption<T>[]
  label?: string
  placeholder?: string
  size?: RichSelectSize
  disabled?: boolean
  class?: string
  error?: string
  hint?: string
  searchable?: boolean
  searchPlaceholder?: string
  'aria-labelledby'?: string | undefined
  'aria-describedby'?: string | undefined
  'aria-invalid'?: boolean | undefined
}

const INDICATOR_DOT: Record<RichSelectIndicator, string> = {
  success: 'bg-[var(--color-success)]',
  available: 'bg-[var(--color-sage-500)]',
  warning: 'bg-[var(--color-warning)]',
  error: 'bg-[var(--color-error-600)]',
  neutral: 'bg-[var(--color-neutral-400)]',
}

const sizeClasses: Record<RichSelectSize, { trigger: string; content: string; input: string }> = {
  sm: {
    trigger: 'px-2.5 py-1.5 text-[length:var(--text-sm)]',
    content: 'text-[length:var(--text-sm)]',
    input: 'px-2.5 py-1.5 text-[length:var(--text-sm)]',
  },
  md: {
    trigger: 'px-3 py-2 text-[length:var(--text-base)]',
    content: 'text-[length:var(--text-sm)]',
    input: 'px-3 py-2 text-[length:var(--text-sm)]',
  },
  lg: {
    trigger: 'px-4 py-3 text-[length:var(--text-base)]',
    content: 'text-[length:var(--text-sm)]',
    input: 'px-3 py-2 text-[length:var(--text-sm)]',
  },
}

export function RichSelect<T extends string = string>(
  props: RichSelectProps<T>
): ReturnType<Component> {
  const [inputValue, setInputValue] = createSignal('')

  const size = () => props.size ?? 'md'
  const searchable = () => props.searchable ?? true

  const selectedOption = createMemo(() => props.options.find((o) => o.value === props.value))

  const filteredOptions = createMemo(() => {
    const query = foldForSearch(inputValue())
    if (!query) {
      return props.options
    }
    return props.options.filter((option) =>
      [option.label, option.description, ...(option.keywords ?? [])].some((text) =>
        foldForSearch(text).includes(query)
      )
    )
  })

  return (
    <div class={`flex flex-col gap-1.5 ${props.class ?? ''}`}>
      <Show when={props.label}>
        <label class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-text)]">
          {props.label}
        </label>
      </Show>

      <Combobox<RichSelectOption<T>>
        multiple={false}
        options={filteredOptions()}
        defaultFilter={() => true}
        optionValue="value"
        optionTextValue="label"
        optionLabel="label"
        optionDisabled="disabled"
        value={selectedOption() ?? null}
        onChange={(option) => {
          if (option) {
            props.onChange(option.value)
          }
        }}
        onInputChange={setInputValue}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            setInputValue('')
          }
        }}
        disabled={props.disabled ?? false}
        validationState={props['aria-invalid'] ? 'invalid' : 'valid'}
        placeholder={props.placeholder ?? 'Select...'}
        itemComponent={(itemProps) => (
          <Combobox.Item
            item={itemProps.item}
            class={`
              flex items-start gap-3 px-3 py-2.5
              cursor-pointer outline-none
              rounded-[var(--radius-md)]
              transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
              hover:bg-[var(--color-neutral-100)]
              data-[highlighted]:bg-[var(--color-neutral-100)]
              data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed
              data-[selected]:bg-[var(--color-neutral-50)]
            `.trim()}
          >
            <Show
              when={itemProps.item.rawValue.leading}
              fallback={
                <Show when={itemProps.item.rawValue.indicator}>
                  {(ind) => (
                    <span
                      data-testid="rich-select-item-dot"
                      class={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${INDICATOR_DOT[ind()]}`}
                    />
                  )}
                </Show>
              }
            >
              {(leading) => <span class="flex flex-shrink-0 items-center">{leading()()}</span>}
            </Show>
            <div class="flex-1 min-w-0">
              <Combobox.ItemLabel class="font-medium text-[color:var(--color-text)] truncate block">
                {itemProps.item.rawValue.label}
              </Combobox.ItemLabel>
              <Show when={itemProps.item.rawValue.description}>
                <p class="text-[length:var(--text-xs)] text-[color:var(--color-text-subtle)] mt-0.5 line-clamp-2">
                  {itemProps.item.rawValue.description}
                </p>
              </Show>
            </div>
            <Combobox.ItemIndicator class="flex-shrink-0 mt-1.5">
              <Check class="w-4 h-4 text-[color:var(--color-primary-500)]" />
            </Combobox.ItemIndicator>
          </Combobox.Item>
        )}
      >
        <Combobox.Control
          class={`
            ds-field w-full
            rounded-[var(--radius-12)]
            border
            bg-[var(--color-surface)]
            font-[family-name:var(--font-sans)]
            transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
            aria-disabled:opacity-50 aria-disabled:cursor-not-allowed
            ${
              props.error
                ? 'border-[var(--color-error-600)] has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-[var(--color-error-600)]'
                : 'border-[var(--color-field-border)] hover:border-[var(--color-field-hover)] focus-within:border-[var(--color-primary-500)] focus-within:shadow-[var(--focus-ring)]'
            }
          `.trim()}
        >
          <Combobox.Trigger
            class={`
              w-full flex items-center justify-between gap-2 outline-none cursor-pointer
              ${sizeClasses[size()].trigger}
            `.trim()}
          >
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <Show
                when={selectedOption()?.leading}
                fallback={
                  <Show when={selectedOption()?.indicator}>
                    {(ind) => (
                      <span
                        data-testid="rich-select-trigger-dot"
                        class={`w-2 h-2 rounded-full flex-shrink-0 ${INDICATOR_DOT[ind()]}`}
                      />
                    )}
                  </Show>
                }
              >
                {(leading) => <span class="flex flex-shrink-0 items-center">{leading()()}</span>}
              </Show>
              <Combobox.Input
                aria-labelledby={props['aria-labelledby']}
                aria-describedby={props['aria-describedby']}
                readOnly={!searchable()}
                class={`
                  flex-1 bg-transparent outline-none cursor-pointer caret-transparent
                  placeholder:text-[color:var(--color-text-muted)]
                  ${!searchable() ? 'select-none' : ''}
                  ${props.value ? 'text-[color:var(--color-text)]' : 'text-[color:var(--color-text-muted)]'}
                `.trim()}
              />
            </div>
            <Combobox.Icon class="flex-shrink-0">
              <CaretDown class="w-4 h-4 text-[color:var(--color-text-muted)] transition-transform duration-[var(--duration-fast)] data-[expanded]:rotate-180" />
            </Combobox.Icon>
          </Combobox.Trigger>
        </Combobox.Control>

        <Combobox.Portal>
          <Combobox.Content
            class={`
              w-[var(--kb-popper-anchor-width)]
              bg-[var(--color-surface)]
              border border-[var(--color-border)]
              rounded-[var(--radius-lg)]
              shadow-[var(--shadow-float)]
              z-50
              animate-[dropdown-enter_150ms_var(--ease-component)]
              overflow-hidden
              ${sizeClasses[size()].content}
            `.trim()}
          >
            <Show when={searchable() && props.options.length > 5}>
              <div class="p-2 border-b border-[var(--color-border)]">
                <div class="relative">
                  <MagnifyingGlass class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[color:var(--color-text-muted)]" />
                  <input
                    type="text"
                    value={inputValue()}
                    onInput={(e) => setInputValue(e.currentTarget.value)}
                    placeholder={props.searchPlaceholder ?? 'Search...'}
                    class={`
                      w-full pl-8 pr-3 py-1.5
                      bg-[var(--color-neutral-50)]
                      border border-[var(--color-field-border)]
                      rounded-[var(--radius-md)]
                      text-[length:var(--text-sm)]
                      placeholder:text-[color:var(--color-text-muted)]
                      focus:outline-none focus:border-[var(--color-primary-500)]
                    `.trim()}
                  />
                </div>
              </div>
            </Show>

            <Combobox.Listbox class="max-h-[240px] overflow-y-auto py-1" />

            <Show when={filteredOptions().length === 0}>
              <div class="px-3 py-4 text-center text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]">
                No options found
              </div>
            </Show>
          </Combobox.Content>
        </Combobox.Portal>
      </Combobox>

      <Show when={props.error}>
        <p class="text-[length:var(--text-sm)] text-[color:var(--color-error-600)]">
          {props.error}
        </p>
      </Show>
      <Show when={props.hint && !props.error}>
        <p class="text-[length:var(--text-sm)] text-[color:var(--color-text-muted)]">
          {props.hint}
        </p>
      </Show>
    </div>
  )
}
